import { cn } from '@invent/wl-ui-kit-next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Sheet,
  SheetContent,
  SheetTitle
} from '@invent/wl-ui-kit-next/ui';
import { ChevronDown } from 'lucide-react';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import {
  CompactBar,
  CompactChipLine,
  CompactDivider,
  CompactMenu,
  CompactSearch,
  CompactTitle,
  FilterBox,
  HelpPopover,
  NotPermitted,
  PageHeader,
  STICKY_BAR_TOP,
  STICKY_TABLE_TOP,
  WorkToolbar,
  useScrolledPast,
  useWorkToolbar,
  type DataTableState,
  type IDataSelection
} from '@/design';
import { useCan } from '@/domains/auth';
import type { IEtagged } from '@/domains/api';
import type {
  IListField,
  IListFieldCatalogue,
  IListFormattingRule,
  IListOrder,
  IListQuery,
  IListRow,
  IListScope,
  IMigratedPart,
  ISavedView
} from '../../api/lists-contracts';
import { listsApi } from '../../api/lists-api';
import { LoadError, PastTheEnd, useAdminData } from '../../components/admin-page';
import {
  NAME_FIELD,
  NO_PROBLEMS,
  criteriaPaths,
  emptyRoot,
  isChanged,
  nameContainsOf,
  rootChips,
  withNameContains,
  withPinned,
  withoutNodes,
  placeProblems,
  readCriteria,
  writeCriteria,
  type CriteriaNode,
  type ICriteriaGroup,
  type ICriteriaProblem,
  type IFilterChip,
  type IPlacedCriteriaProblems
} from '../../list-criteria';
import {
  ALL_SURVEYS,
  DEFAULT_LIST_PAGE_SIZE,
  LIST_MAX_PAGE_SIZE,
  LIST_PAGE_SIZES,
  LIST_SURVEYS,
  type IListSurvey,
  TEAMS_SURVEY,
  TEAM_PATH_CHOICES,
  catalogueSections,
  countOf,
  formatListValue,
  listSurveyLabel,
  lostSummary,
  migratedPartLabel
} from '../../lists';
import { recordSurveyPath } from '../../components/participant-link';
import { runSaid, type IRecycleRun } from '../../recycle-bin';
import { ListActions } from './actions';
import { AdvancedFilterSheet, builderFields } from './advanced-filter';
import { ExportDialog } from './export-dialog';
import { FormattingSheet, ruleProblems } from './formatting-sheet';
import { RowNotesDialog } from './row-notes';
import { SaveAsReportDialog, useViewSaves } from './save-view';
import type { ICellEdit } from './cells';
import { ColumnsSheet } from './columns-sheet';
import { ConflictDialog } from './conflict-dialog';
import {
  ASSIGNED_TO,
  answerTagOf,
  draftKey,
  editKindOf,
  isAnswerField,
  nameOf,
  useListEdits
} from './editing';
import { FiltersButton, listFilterChips } from './filter-chips';
import { hasFunnel } from './funnel';
import { ListGrid, type IFunnelOpen } from './grid';
import { GroupStrip } from './group-by';
import { Legend } from './legend';
import { PaneControl, ReadingPane, useAutosavePref, useMediaQuery, usePanePrefs, type PaneMode } from './pane';
import { ListViewBar } from './toolbar';
import { useCatalogueLookups, useCriteriaWords } from './use-catalogue';
import { rowKeyOf, useListRows } from './use-list-rows';
import { ViewPicker } from './view-picker';
import type { IWorkSearchBox } from './work-page/controls';
import { WorkPageHeaderActions } from './work-page/header-actions';
import { WorkPager, dropPaging } from './work-page/pager';
import { SelectionBar } from './work-page/selection-bar';

/**
 * Lists (SR-230, design `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md` §3) — the
 * Conga grid the research team runs the cycle in, live over survey-api's list engine: a survey's
 * saved views, a funnel on every column, the filter in words, grouping, Σ totals and a 30 s poll.
 * It replaces the demo's Worklists mock (SR-221), whose shape and words it keeps.
 *
 * **The address is the survey and the view** (§3.1): `?survey=` one of `LIST_SURVEYS` or `all`, FA
 * when absent; `?view=` a view's id. With no `view` the survey's default view opens while the
 * caller has opened none of the survey, else the one opened last — survey-api lists the opened ones
 * first, newest first. A `view` the caller cannot open says "No such view" and opens the default.
 * So a link to a view is shareable and the back button walks views; **nothing else of the screen is
 * in the address** — a filter, a column or a sort changed is page state, marked *Changed — not
 * saved*, and a reload loses it, as Conga's `*Unsaved View` did.
 *
 * **A survey this deployment does not hold** — its catalogue answers 404; `local` has no Canada —
 * draws its tab and "This survey is not on this deployment", and nothing else.
 *
 * **One screen per view opened** (`ListScreen`, keyed by the view and its `ETag`): what a reader
 * changed belongs to the view it was changed on, and a view opened afresh starts from what it holds.
 * **Cells being edited are not dropped unasked** (the owner's ruling, 2026-10-09): while drafts are
 * pending, opening another view (the picker, "Select a view") or another survey's tab asks first —
 * "Discard N unsaved changes?", *Discard* or *Cancel*, which keeps the screen and its drafts. The
 * survey tab already open is no link at all: following it would drop the view and open the screen
 * afresh.
 *
 * **The writes** (§3.6, §3.9, §3.10): an editable cell's control stages a draft (`editing.ts`),
 * *Save data* writes them as one batch and asks about each conflict in turn, *Actions ▾* stages a
 * value on the ticked rows or sets one on every row, or moves their responses to the recycle bin
 * (SR-470's D6) — the moved rows leave on the page read afresh, and the outcome is said in the
 * status line — and the reading pane reads a row as a record or edits it. **Every decision reads
 * `useCan()`** (§3.12): without `internal_fields.write` every cell is read, *Save data* and
 * *Edit* are disabled and one `NotPermitted` says why; without `responses.annotate` an answer's
 * cell is read, said the same way (SR-403); without `lists.assign` the *Assigned to* cells are
 * read and say so too.
 *
 * **The view's saves** (§3.3, `save-view.tsx`) are taken in place: a *Save*, *Rename…*, *Make
 * default* or *Reload* replaces the view the screen compares with, and *Save as…* or *Delete…* hands
 * the screen to the new view, or to none, without opening it afresh (`ListOpening`'s `hand`) — so the
 * cells being edited, the page and the pane stay. *Formatting…* edits the screen's rules; *Export…*
 * and *Save as a report…* are *Actions ▾*'s (§3.10, §3.11).
 */
export function AdminListsPage() {
  const [params, setParams] = useSearchParams();
  const [headerHost, setHeaderHost] = React.useState<Nullable<HTMLElement>>(null);
  const guard = useDraftsGuard();
  const survey = params.get('survey') || 'FA';
  const viewParam = params.get('view');
  // The catalogue's 404 is an answer — this survey is not here — so the read says which survey it
  // answered, and `null` inside it is that answer rather than a read still out.
  const catalogue = useAdminData(
    async (signal) => ({ survey, fields: await listsApi.fields(survey, signal) }),
    [survey]
  );
  const answer = catalogue.data?.survey === survey ? catalogue.data : null;

  return (
    <>
      <PageHeader
        title="Lists"
        crumb="Cycle"
        row
        tabs={<SurveyTabs current={survey} guard={guard.ask} />}
        actions={<span ref={setHeaderHost} data-lists-header="" className="flex min-w-0 items-center gap-3" />}
      />
      <HeaderHost.Provider value={headerHost}>
      <div className="grid min-w-0 gap-3 px-6 py-4">
        {catalogue.error ? (
          <LoadError message={`The list’s fields could not be read. ${catalogue.error}`} />
        ) : answer === null ? (
          <p role="status" className="py-8 text-center text-muted-foreground">
            Reading the list…
          </p>
        ) : answer.fields === null ? (
          <p role="status" className="py-8 text-center text-muted-foreground">
            This survey is not on this deployment.
          </p>
        ) : (
          <ListOpening
            key={survey}
            survey={survey}
            viewParam={viewParam}
            catalogue={answer.fields}
            guard={guard}
            onOpenView={(viewId, viewSurvey = survey) =>
              setParams({ survey: viewSurvey, view: viewId }, { replace: viewSurvey !== survey })
            }
            onMove={(viewId) => setParams(viewId ? { survey, view: viewId } : { survey })}
          />
        )}
      </div>
      </HeaderHost.Provider>
      {guard.dialog}
    </>
  );
}

/** Where the screen draws its part of the header — the row count, the "?" and *Refresh*, which it
 *  hands to the app bar (work-pages design, "Anatomy" 1). The header is the page's; what it says is
 *  the open view's. */
const HeaderHost = React.createContext<Nullable<HTMLElement>>(null);

/** The drafts' guard (the owner's ruling): the screen says how many cells are pending, and a move
 *  that would drop them asks first. */
interface IDraftsGuard {
  /** Runs `go` at once while nothing is pending, else once *Discard* is pressed. */
  ask: (go: () => void) => void;
  /** The screen's pending count, told on each change. */
  pending: (count: number) => void;
  dialog: React.ReactNode;
}

function useDraftsGuard(): IDraftsGuard {
  const pendingRef = React.useRef(0);
  const [asking, setAsking] = React.useState<Nullable<{ count: number; go: () => void }>>(null);
  const ask = React.useCallback((go: () => void) => {
    if (pendingRef.current > 0) setAsking({ count: pendingRef.current, go });
    else go();
  }, []);
  const pending = React.useCallback((count: number) => {
    pendingRef.current = count;
  }, []);
  const dialog = (
    <AlertDialog open={asking !== null} onOpenChange={(open) => !open && setAsking(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Discard {countOf(asking?.count ?? 0, 'unsaved change')}?</AlertDialogTitle>
          <AlertDialogDescription>
            {asking?.count === 1 ? 'The cell is' : 'The cells are'} not saved — Save data writes
            them. Opening another view or survey drops {asking?.count === 1 ? 'it' : 'them'}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              const go = asking?.go;
              setAsking(null);
              go?.();
            }}
          >
            Discard
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
  return { ask, pending, dialog };
}

/** The survey tabs (LST-04) as routed links — each drops the view, which is the survey's own. A
 *  `survey` in the address no tab knows gets a tab of its own, spelled as sent. With a `guard`
 *  (Lists), a press goes through it, and the tab already open does nothing. `surveys` narrows the
 *  tabs where a screen reads fewer — Reinstatement, *All surveys* and Canada aside. `searchOf`
 *  writes a tab's address where a screen keeps the rest of its own (DD calls), `replace` in place. */
export function SurveyTabs({
  current,
  guard,
  surveys = [...LIST_SURVEYS, ALL_SURVEYS],
  searchOf = (code) => `?survey=${encodeURIComponent(code)}`,
  replace
}: {
  /** null: no tab is open — Reports' search, which reads every survey (SR-462). */
  current: Nullable<string>;
  guard?: (go: () => void) => void;
  surveys?: readonly IListSurvey[];
  searchOf?: (code: string) => string;
  replace?: boolean;
}) {
  const navigate = useNavigate();
  const known = surveys;
  const tabs = current === null || known.some((tab) => tab.code === current)
    ? known
    : [...known, { code: current, label: listSurveyLabel(current) }];
  return (
    <nav aria-label="Surveys" className="-mx-1 flex flex-wrap gap-x-4">
      {tabs.map((tab) => {
        const active = tab.code === current;
        const to = { search: searchOf(tab.code) };
        return (
          <Link
            key={tab.code}
            to={to}
            replace={replace}
            onClick={(event) => {
              const plain = event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
              if (!guard || !plain) return;
              event.preventDefault();
              if (!active) guard(() => navigate(to));
            }}
            aria-current={active ? 'page' : undefined}
            className={cn(
              '-mb-px inline-flex items-baseline gap-1.5 border-b-2 px-1 pt-1 pb-2 text-[0.8125rem] transition-colors',
              active
                ? 'border-[var(--color-teal)] font-medium text-[var(--color-royal-blue)]'
                : 'border-transparent text-muted-foreground hover:text-[var(--color-royal-blue)]'
            )}
          >
            {tab.label}
          </Link>
        );
      })}
    </nav>
  );
}

/** `SurveyTabs` in a work page's compact bar: the survey open, the others a menu away. */
export function SurveyMenu({
  current,
  surveys = [...LIST_SURVEYS, ALL_SURVEYS],
  searchOf = (code) => `?survey=${encodeURIComponent(code)}`,
  replace
}: {
  current: string;
  surveys?: readonly IListSurvey[];
  searchOf?: (code: string) => string;
  replace?: boolean;
}) {
  return (
    <CompactMenu
      label="Survey"
      trigger={listSurveyLabel(current)}
      items={surveys.map((entry) => ({
        key: entry.code,
        label: entry.label,
        to: { search: searchOf(entry.code) },
        replace,
        current: entry.code === current
      }))}
    />
  );
}

/** The view a survey's Lists open on, and whether the one the address named could not be — or,
 *  where it is another survey's view, that survey, whose tab it opens under. */
interface IOpened {
  view: Nullable<IEtagged<ISavedView>>;
  noSuchView: boolean;
  elsewhere?: string;
  /** The screen drawn for this opening — a view handed over in place keeps the screen it came
   *  from. */
  key: number;
  /** *New list* (SR-412): a bare list on a screen of its own, not saved yet. */
  fresh?: boolean;
}

/**
 * Which view opens (§3.1): the address's, else the one opened last, else the default. The open is
 * survey-api's `GET /views/{id}`, which stamps it — so it is asked once per address, and the
 * picker's list, read when it opens, already has it first. A view of another survey — a link
 * pasted without its `survey` — moves the address to that survey's tab, in place.
 *
 * **A view handed over is not opened again** (`hand`): *Save as…* made it and *Delete…* left none,
 * and the screen already shows what it holds, cells being edited included. The address moves to it
 * — the back button still walks views — and the read that the move asks for answers the view handed
 * over, under the screen it came from, so nothing remounts.
 */
function ListOpening({
  survey,
  viewParam,
  catalogue,
  guard,
  onOpenView,
  onMove
}: {
  survey: string;
  viewParam: Nullable<string>;
  catalogue: IListFieldCatalogue;
  guard: IDraftsGuard;
  onOpenView: (viewId: string, viewSurvey?: string) => void;
  /** The address to a view handed over, or to none — no read of it. */
  onMove: (viewId: Nullable<string>) => void;
}) {
  const handed = React.useRef<Nullable<{ viewId: Nullable<string>; opened: IOpened }>>(null);
  const screens = React.useRef(0);
  const opened = useAdminData<IOpened>(
    async (signal) => {
      const held = handed.current;
      if (held !== null && held.viewId === viewParam) return held.opened;
      handed.current = null;
      const key = ++screens.current;
      let noSuchView = false;
      if (viewParam !== null) {
        const asked = await listsApi.openView(viewParam, signal);
        if (asked !== null && asked.body.query.survey !== survey) {
          return { view: null, noSuchView, elsewhere: asked.body.query.survey, key };
        }
        if (asked !== null) return { view: asked, noSuchView, key };
        noSuchView = true;
      }
      const views = (await listsApi.listViews(survey, 'list', signal)) ?? [];
      // The list is survey-api's order: the opened ones first, newest first.
      const last = noSuchView ? undefined : views.find((view) => view.openedAt !== null);
      const pick = last ?? views.find((view) => view.isDefault);
      const view = pick ? await listsApi.openView(pick.id, signal) : null;
      return { view, noSuchView, key };
    },
    [survey, viewParam]
  );
  const hand = (view: Nullable<IEtagged<ISavedView>>) => {
    const current = opened.data;
    if (current === null) return;
    const next: IOpened = { view, noSuchView: false, key: current.key };
    handed.current = { viewId: view?.body.id ?? null, opened: next };
    opened.adopt(next);
    onMove(view?.body.id ?? null);
  };

  /** *New list*: a new screen, bare (`startOf` with no view), and the address with no view. */
  const startNew = () => {
    const next: IOpened = { view: null, noSuchView: false, key: ++screens.current, fresh: true };
    handed.current = { viewId: null, opened: next };
    opened.adopt(next);
    onMove(null);
  };

  const elsewhere = opened.data?.elsewhere;
  React.useEffect(() => {
    if (elsewhere !== undefined && viewParam !== null) onOpenView(viewParam, elsewhere);
    // Once per answer: the address moves, and this component goes with the old survey's tab.
  }, [elsewhere]);

  if (opened.error) {
    return <LoadError message={`This list could not be opened. ${opened.error}`} />;
  }
  if (opened.data === null || elsewhere !== undefined) {
    return (
      <p role="status" className="py-8 text-center text-muted-foreground">
        Opening the view…
      </p>
    );
  }
  const { view, noSuchView } = opened.data;
  return (
    <>
      {noSuchView && (
        <p role="alert" className="text-sm text-destructive">
          No such view — {view ? 'the survey’s default view is open instead.' : 'and this survey has no default view.'}
        </p>
      )}
      <ListScreen
        key={opened.data.key}
        survey={survey}
        catalogue={catalogue}
        opened={view}
        fresh={opened.data.fresh === true}
        guard={guard}
        onOpenView={onOpenView}
        onHand={hand}
        onNew={startNew}
      />
    </>
  );
}

/** From this width the header holds the view picker and *Save view ▾* (SR-412); below it they are a
 *  line of their own, the header having no room beside the survey tabs. */
const VIEW_IN_HEADER = '(min-width: 1366px)';

/** How long the name box waits after the last key before the list follows it (SR-412). */
const NAME_SEARCH_DELAY_MS = 600;

/** How long a private view waits after the last change before it saves itself (SR-412). */
const AUTOSAVE_DELAY_MS = 1200;

/** survey-api's *Survey* field — the column *All surveys* opens with (§3.2 item 1). */
export const SURVEY_FIELD = 'sys.survey';

/** The columns a list opens with where no view opens: survey-api's seeded views' own core
 *  (`ListViewSeeds` — Name, Firm, State, Status) and the day submitted, with on *All surveys* each
 *  row's survey. A name alone looked like an empty screen; a field the catalogue lacks is left out. */
const BARE_COLUMNS = ['sys.firm_name', 'sys.state', 'sys.status', 'sys.submitted_at'];

/** What the screen starts from: the view's query, page size and rules — or, for a survey with no
 *  view opened and no default, and for a new report (`ReportScreen`), a bare list (`BARE_COLUMNS`).
 *  A view's own columns are its own, none included. */
export function startOf(view: Nullable<ISavedView>, fields: ReadonlyMap<string, IListField>, survey: string) {
  const query = view?.query;
  const surveyColumn = survey === ALL_SURVEYS.code ? [SURVEY_FIELD] : [];
  const bare = [NAME_FIELD, ...surveyColumn, ...BARE_COLUMNS].filter((code) => fields.has(code));
  return {
    root: readCriteria(query?.criteria ?? null),
    pinned: readCriteria(view?.pinned ?? null),
    columns: view ? (query?.columns ?? []) : bare,
    sorts: query?.sorts ?? [],
    groupBy: query?.groupBy ?? [],
    scope: query?.scope ?? null,
    pageSize: view?.pageSize ?? null,
    rules: view?.formattingRules ?? []
  };
}

/**
 * One view on screen: the filter, columns, sorts, grouping and page size as the reader changed
 * them — the view's until they differ, then *Changed — not saved* — and the page read under them.
 */
function ListScreen({
  survey,
  catalogue,
  opened,
  fresh,
  guard,
  onOpenView,
  onHand,
  onNew
}: {
  survey: string;
  catalogue: IListFieldCatalogue;
  /** The view as opened, with its `ETag`; the screen then holds it, and its saves replace it. */
  opened: Nullable<IEtagged<ISavedView>>;
  /** Opened by *New list* — said so until it is saved as a view. */
  fresh: boolean;
  /** *New list*, asked past the pending drafts. */
  onNew: () => void;
  /** Told the pending drafts; asked before another view opens. */
  guard: IDraftsGuard;
  onOpenView: (viewId: string) => void;
  /** A view made by *Save as…*, or none after *Delete…*, handed to the address in place. */
  onHand: (view: Nullable<IEtagged<ISavedView>>) => void;
}) {
  const [saved, setSaved] = React.useState(opened);
  const view = saved?.body ?? null;
  const { fields, fieldOf, sourceOf, kindOf, assignees, accounts, emailOf, words: lookupWords } =
    useCatalogueLookups(catalogue);
  // The accounts' read failing is said, and nothing is assigned over it (§3.6).
  const accountsUnread = assignees.error !== null;

  const [start] = React.useState(() => startOf(view, fields, survey));
  const [root, setRoot] = React.useState<ICriteriaGroup>(start.root);
  // The view's pinned conditions (SR-412): every read ANDs them with the screen's own; who may save
  // over a shared view pins and unpins, everyone else reads them locked.
  const [pinned, setPinned] = React.useState<ICriteriaGroup>(start.pinned);
  const readRoot = withPinned(pinned, root);
  const words = useCriteriaWords(survey, readRoot, lookupWords);
  const [columns, setColumns] = React.useState<string[]>(start.columns);
  const [sorts, setSorts] = React.useState<IListOrder[]>(start.sorts);
  const [groupBy, setGroupBy] = React.useState<IListOrder[]>(start.groupBy);
  const [scope, setScope] = React.useState<Nullable<IListScope>>(start.scope);
  const [pageSize, setPageSize] = React.useState<Nullable<number>>(start.pageSize);
  const [rules, setRules] = React.useState<IListFormattingRule[]>(start.rules);
  const [formatting, setFormatting] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const [reporting, setReporting] = React.useState(false);
  const [noting, setNoting] = React.useState<Nullable<IListRow>>(null);
  const [params, setParams] = useSearchParams();
  const [collapsed, setCollapsed] = React.useState<ReadonlySet<string>>(new Set());
  const panePrefs = usePanePrefs();
  const viewOpens = panePrefs.opens;
  const [advanced, setAdvanced] = React.useState<{ open: boolean; focusId?: string; addField?: string }>({
    open: false
  });

  const setWindow = (nextPage: number, nextPages = 1) =>
    setParams(
      (now) => {
        const next = dropPaging(new URLSearchParams(now));
        if (nextPage > 1) next.set('page', String(nextPage));
        if (nextPages > 1) next.set('size', String(nextPages * (pageSize ?? DEFAULT_LIST_PAGE_SIZE)));
        return next;
      },
      { replace: true }
    );
  const setPage = (next: number) => {
    if (next !== page || pages > 1) setWindow(next);
  };
  /** A change of what the list shows starts again at page 1: page 3 of the old list means nothing. */
  const change =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value);
      setPage(1);
    };

  const query: IListQuery = {
    survey,
    scope,
    criteria: writeCriteria(root, kindOf),
    columns,
    sorts,
    groupBy
  };
  const pinnedWire = pinned.children.length > 0 ? writeCriteria(pinned, kindOf) : null;
  const changed =
    view !== null && isChanged(view, { query, pageSize, formattingRules: rules, pinned: pinnedWire });
  // What the list reads, exports and updates: the screen's filter under the pinned one, as the
  // chips show them — the pins on screen, saved or not, so the rows follow a pin or an unpin at
  // once. The read names no view, so survey-api adds no pin of its own.
  const listQuery: IListQuery = pinnedWire ? { ...query, criteria: writeCriteria(readRoot, kindOf) } : query;
  const canPin = view !== null && view.mayEdit && view.visibility !== 'private';
  // A private view of the reader's own saves itself as it changes (SR-412), while the reader's
  // *Autosave my private views* is on; a shared one waits for *Save view*.
  const [autosaveOn, setAutosaveOn] = useAutosavePref();
  const autosaves = autosaveOn && view !== null && view.mayEdit && view.visibility === 'private';

  // The read asks for every field its cells need: the columns, and what a grouping or a rule reads.
  // The name too, whatever the view shows: a selection, the pane and a conflict name the person.
  const needed = [
    ...columns,
    NAME_FIELD,
    ...groupBy.map((level) => level.field),
    ...rules.flatMap((rule) => (rule.field ? [rule.field] : []))
  ].filter((code, index, all) => fields.has(code) && all.indexOf(code) === index);
  const extra = needed.filter((code) => !columns.includes(code));
  const readQuery: IListQuery = { ...listQuery, columns: [...columns, ...extra] };
  const shownColumns = columns.flatMap((code) => fields.get(code) ?? []);
  const hiddenColumns = columns.filter((code) => !fields.has(code)).length;
  const sums = shownColumns.filter((field) => field.summable);
  const size = pageSize ?? DEFAULT_LIST_PAGE_SIZE;
  // The window is the address's (`page`, and `size` after *Load more*), the work pages' way; the
  // page size is the view's (SR-412). *Load more* reads one longer page from the top, so it is
  // offered on page 1 only.
  const page = Math.max(1, Math.floor(Number(params.get('page'))) || 1);
  const shown = Math.min(LIST_MAX_PAGE_SIZE, Number(params.get('size')) || size);
  const pages = page === 1 ? Math.max(1, Math.floor(shown / size)) : 1;

  const list = useListRows({
    query: readQuery,
    page,
    pageSize: pages * size,
    sums: sums.map((field) => field.code)
  });
  const { read, error } = list;

  /** A private copy's refusal (`SaveAsDialog`), its path in the folded tree moved onto the tree the
   *  screen holds the condition in — the screen's own (`query.criteria`) or the pinned one. */
  const unfold = (target: string): string => {
    const held = new Map([...criteriaPaths(root), ...criteriaPaths(pinned, 'pinned')]);
    let best: Nullable<{ from: string; to: string }> = null;
    for (const [id, from] of criteriaPaths(andWith(root, pinned.children))) {
      const to = held.get(id);
      const under = target === from || target.startsWith(`${from}.`) || target.startsWith(`${from}[`);
      if (to !== undefined && under && (best === null || from.length > best.from.length)) best = { from, to };
    }
    return best === null ? target : best.to + target.slice(best.from.length);
  };

  const can = useCan();
  // A private view's own save (SR-412): "Saving…", then a quiet "Saved" where *Changed — not saved*
  // would be — or "Not saved", the refusal in its title, and *Save view* says the view changed again.
  const [autoSaid, setAutoSaid] = React.useState<Nullable<'saving' | 'saved' | 'failed'>>(null);
  const [autoRefusal, setAutoRefusal] = React.useState('');
  const autoFailed = autosaves && changed && autoSaid === 'failed';
  const viewSaves = useViewSaves({
    // A shared view changed and a new list not saved yet say it on *Save view* (SR-412); a private
    // view of the reader's saves itself and says "Saving…" / "Saved" instead, until a save is refused.
    unsaved: (view !== null && changed && (!autosaves || autoFailed)) || (fresh && view === null),
    footer: (
      <DropdownMenuCheckboxItem
        checked={autosaveOn}
        onCheckedChange={(next) => setAutosaveOn(next === true)}
        onSelect={(event) => event.preventDefault()}
      >
        <span className="grid">
          Autosave my private views
          <span className="text-xs text-muted-foreground">Your setting — a shared view always waits for Save</span>
        </span>
      </DropdownMenuCheckboxItem>
    ),
    kind: 'list',
    survey,
    saved,
    changed,
    screen: () => ({
      query,
      pageSize,
      formattingRules: rules,
      pinned: pinnedWire,
      folded: { ...query, criteria: writeCriteria(andWith(root, pinned.children), kindOf) },
      unfold
    }),
    pageSize: size,
    canShare: can('lists.share'),
    sayProblem: (problem) => sayProblem(problem, query, fieldOf),
    onAdopt: setSaved,
    onCreated: (created) => {
      setSaved(created);
      // A private copy holds the pins in its own filter (`SaveAsDialog`): so does the screen.
      if (pinnedWire !== null && !created.body.pinned) {
        setRoot(andWith(root, pinned.children));
        setPinned(emptyRoot());
      }
      // The screen is taken over in place, and the dialog's page size is the new view's — a view
      // saved with the size the screen was showing by default would otherwise read as changed.
      setPageSize(created.body.pageSize);
      onHand(created);
    },
    onDeleted: () => {
      setSaved(null);
      onHand(null);
    },
    onFormatting: () => setFormatting(true),
    // A private view of the reader's saves itself (SR-412): its menu says so.
    menuLabel: autosaves ? (
      <span className="inline-flex items-center gap-1.5" title="Your private view saves itself as you change it">
        Save view
        <span data-autosave-badge="" className="rounded-full bg-[var(--color-slight-blue)] px-1.5 text-[0.6875rem] font-medium text-[var(--color-royal-blue)]">
          Auto
        </span>
      </span>
    ) : undefined
  });
  // A moment after the last change, once per state of the screen: a state already sent or refused
  // is not sent again, and a change made while a save is out waits for it to land (`busy`), then
  // goes. The timer calls the latest `autosave`, which quotes the latest revision.
  const screenState = autosaves && changed ? JSON.stringify([query, pageSize, rules, pinnedWire]) : '';
  const sentState = React.useRef('');
  const refusedState = React.useRef('');
  const owed = React.useRef(false);
  owed.current = screenState !== '' && screenState !== sentState.current && screenState !== refusedState.current;
  const savesNow = React.useRef(viewSaves);
  savesNow.current = viewSaves;
  // A screen whose read survey-api refused (a condition it cannot take) is not saved: its chips
  // already say why, and the save would only be refused for the same reason.
  const readRefused = React.useRef(false);
  readRefused.current = (error?.problems ?? []).some((problem) => problem.target.startsWith('query.'));
  React.useEffect(() => {
    if (!owed.current || viewSaves.busy) return;
    const timer = window.setTimeout(() => {
      if (readRefused.current) return;
      const saving = savesNow.current.autosave();
      if (saving === null) return;
      sentState.current = screenState;
      setAutoSaid('saving');
      void saving.then((refusal) => {
        if (refusal === null) {
          setAutoSaid('saved');
          return;
        }
        refusedState.current = screenState;
        setAutoRefusal(refusal);
        setAutoSaid('failed');
      });
    }, AUTOSAVE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [screenState, viewSaves.busy]);
  // A change still waiting for its moment is saved as the screen is left — another view, survey or
  // page.
  React.useEffect(
    () => () => {
      if (owed.current && !readRefused.current) savesNow.current.flush();
    },
    []
  );
  const canWrite = can('internal_fields.write');
  const canAssign = can('lists.assign');
  const canBulk = can('lists.bulk');
  // An answer cell is survey-api's correction, the Answers tab's own policy (SR-403).
  const canCorrect = can('responses.annotate');
  const writeReason = React.useId();
  const assignReason = React.useId();
  const correctReason = React.useId();
  const edits = useListEdits({
    viewId: view?.id ?? null,
    emailOf,
    adopt: list.adopt,
    poll: list.poll,
    refresh: list.refresh
  });
  const editOf = (row: IListRow, field: IListField): Nullable<ICellEdit> => {
    const kind = editKindOf(field);
    if (kind === null || !canWrite || (kind === 'assignee' && (!canAssign || accountsUnread))) return null;
    if (!canCorrect && isAnswerField(field)) return null;
    const key = draftKey({ ...row, field: field.code });
    return {
      kind,
      draft: edits.drafts.get(key),
      error: edits.cellErrors.get(key),
      accounts,
      onStage: (ui, badInput) => edits.stage(row, field, ui, badInput)
    };
  };

  // The ticked rows and what the last press said belong to the list read: a new query, page or
  // Refresh starts without them.
  const [ticked, setTicked] = React.useState<ReadonlySet<string>>(new Set());
  const [massSaid, setMassSaid] = React.useState<Nullable<string>>(null);
  const [recycleSaid, setRecycleSaid] = React.useState<Nullable<IRecycleRun>>(null);
  const { clearSaid } = edits;
  React.useEffect(() => {
    setTicked(new Set());
    setMassSaid(null);
    setRecycleSaid(null);
    clearSaid();
  }, [list.asked, clearSaid]);
  const selection: IDataSelection<IListRow> = {
    selected: ticked,
    onSelect: (keys) => setTicked(new Set(keys)),
    name: nameOf
  };
  const tickedRows = (read?.rows ?? []).filter((row) => ticked.has(rowKeyOf(row)));
  const editableSections = catalogueSections(
    catalogue,
    (field) => editKindOf(field) !== null && (canCorrect || !isAnswerField(field))
  );
  const answerShown = shownColumns.some((field) => answerTagOf(field) !== null);
  const answersLocked =
    !canCorrect && catalogue.groups.some((group) => group.fields.some((field) => answerTagOf(field) !== null));

  const [pane, setPane] = React.useState<Nullable<{ key: string; mode: PaneMode }>>(null);
  /** The pane's row where it is on this page; the pane is drawn while there is one. */
  const paneRow = pane ? (read?.rows.find((row) => rowKeyOf(row) === pane.key) ?? null) : null;
  const openPane = (row: IListRow, mode: PaneMode) => setPane({ key: rowKeyOf(row), mode });
  const viewRow = (row: IListRow) => openPane(row, 'view');
  /** ↑/↓ in the grid move the pane drawn with the focused row, in the mode it is in — only one
   *  drawn: a pane whose row left the page is hidden. */
  const follow = (event: React.FocusEvent) => {
    const target = event.target as HTMLElement;
    const key = target.matches('tr[data-row-key]') ? target.getAttribute('data-row-key') : null;
    if (key !== null && paneRow && pane && key !== pane.key) setPane({ ...pane, key });
  };
  const pendingCount = edits.drafts.size;
  const { pending: tellPending } = guard;
  React.useEffect(() => {
    tellPending(pendingCount);
    return () => tellPending(0);
  }, [pendingCount, tellPending]);
  // What a mass update counts and an export says: this query's total, never the last query's while
  // this one's read is out — the press sends this query.
  const total = read !== null && read.asked === list.asked ? read.total : null;

  // The criteria a read or a save refused land on their chips and builder rows alike, a pinned one's
  // on its pinned chip; the rest of a save's refusals are said in its line (`viewSaves.notice`). A
  // save sends the two trees apart (`query.criteria`, `pinned`); a read sends them ANDed
  // (`readRoot`), so its paths are that tree's.
  const readProblems: ICriteriaProblem[] = [];
  const otherProblems: string[] = [];
  for (const problem of error?.problems ?? []) {
    if (problem.target.startsWith('query.criteria')) readProblems.push(problem);
    else otherProblems.push(sayProblem(problem, readQuery, fieldOf));
  }
  const savedOn = (prefix: string) => viewSaves.problems.filter((problem) => problem.target.startsWith(prefix));
  const placed = joinPlaced([
    placeProblems(savedOn('query.criteria'), root),
    placeProblems(savedOn('pinned'), pinned, criteriaPaths(pinned, 'pinned')),
    placeProblems(readProblems, readRoot, criteriaPaths(readRoot))
  ]);
  const problemsOf = (nodeId: string) => placed.byNode.get(nodeId) ?? [];
  const openAdvanced = (focusId?: string) => setAdvanced({ open: true, focusId });
  // Which column's funnel is open — the screen's, so a chip's press opens its column's (§3.4).
  const [funnel, setFunnel] = React.useState<Nullable<IFunnelOpen>>(null);
  /** A chip's press: its column's funnel where the grid draws one — closed again by a second
   *  press, as by its header's button — else *Advanced…* on its node: a column not shown, or one
   *  that filters on nothing. */
  const pressChip = (chip: IFilterChip, from: HTMLElement) => {
    const column =
      chip.door === 'funnel' ? shownColumns.find((field) => field.code === chip.field) : undefined;
    if (column && hasFunnel(column)) {
      setFunnel(funnel?.code === column.code ? null : { code: column.code, returnTo: from });
    } else openAdvanced(chip.nodeIds[0]);
  };
  /** A field chosen under *Filters* (SR-412): its column's funnel where the grid draws one, else
   *  *Advanced…* with a new condition on it. */
  const filterOn = (code: string, from: HTMLElement) => {
    const column = shownColumns.find((field) => field.code === code);
    if (column && hasFunnel(column)) setFunnel({ code, returnTo: from });
    else setAdvanced({ open: true, addField: code });
  };
  const filterSections = React.useMemo(() => builderFields(catalogue).sections, [catalogue]);

  const struckOnPage = read ? read.rows.filter((row) => list.struck.has(row.participantId)).length : 0;
  let tableState: DataTableState | undefined;
  if (read === null && error) {
    const said =
      error.problems.length > 0
        ? `This list could not be read: ${
            [...placed.unplaced.map((problem) => sayProblem(problem, readQuery, fieldOf)), ...otherProblems].join(
              '; '
            ) || 'the conditions marked cannot be used — change them or ✕ them.'
          }`
        : `This list could not be read. ${error.message}`;
    tableState = { kind: 'error', content: <LoadError message={said} /> };
  } else if (read === null) {
    tableState = { kind: 'empty', content: 'Reading the list…' };
  } else if (read.rows.length === 0 && read.total > 0) {
    tableState = {
      kind: 'empty',
      content: <PastTheEnd page={page} pageSize={size} total={read.total} counted={['row', 'rows']} onPage={setPage} />
    };
  } else if (read.rows.length === 0) {
    tableState = { kind: 'empty', content: 'No row matches this list.' };
  }

  // The name search (SR-412): the work pages' box over `sys.display_name contains`; it makes no
  // chip — the box shows what the filter holds, *Clear all* empties it.
  const heldName = nameContainsOf(root);
  const [nameDraft, setNameDraft] = React.useState(heldName);
  React.useEffect(() => setNameDraft(heldName), [heldName]);
  // No *Filter* button (SR-412): the list follows the box a moment after the typing stops, and at
  // once on Enter. The latest tree is read at the timer, so a filter changed meanwhile stays.
  const rootNow = React.useRef(root);
  rootNow.current = root;
  React.useEffect(() => {
    const next = nameDraft.trim();
    if (next === nameContainsOf(rootNow.current)) return;
    const timer = window.setTimeout(() => change(setRoot)(withNameContains(rootNow.current, next)), NAME_SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [nameDraft]);
  const nameSearch: IWorkSearchBox = {
    label: 'Name contains',
    placeholder: 'Filter by name…',
    draft: nameDraft,
    onDraft: setNameDraft,
    onSubmit: () => {
      const next = nameDraft.trim();
      if (next !== heldName) change(setRoot)(withNameContains(root, next));
    }
  };
  const chips = listFilterChips({
    root,
    words,
    pinned,
    problemsOf,
    onChange: change(setRoot),
    onChipPress: pressChip,
    canPin: canPin && root.op === 'and',
    onPin: (ids) => {
      change(setRoot)(withoutNodes(root, ids));
      setPinned({ ...pinned, children: [...pinned.children, ...root.children.filter((node) => ids.includes(node.id))] });
    },
    onUnpin: canPin
      ? (ids, keep) => {
          setPinned(withoutNodes(pinned, ids));
          if (!keep) {
            setPage(1);
            return;
          }
          change(setRoot)(andWith(root, pinned.children.filter((node) => ids.includes(node.id))));
        }
      : undefined
  });
  const clearAll = () => change(setRoot)(emptyRoot());
  const chipProblems = [...rootChips(pinned, words), ...rootChips(root, words)].flatMap((chip) =>
    chip.nodeIds.flatMap((id) => problemsOf(id).map((reason) => `${chip.words}: ${reason}`))
  );

  const toolbar = useWorkToolbar();
  const viewInHeader = useMediaQuery(VIEW_IN_HEADER);
  // Teams' *Team path* as one menu (SR-412): three pills took the toolbar past its width.
  const teamPath = survey === TEAMS_SURVEY && (
    <TeamPathMenu path={scope?.path ?? null} onChoose={(path) => change(setScope)({ ...(scope ?? {}), path })} />
  );
  const autoMark =
    autosaves && (changed || autoSaid === 'saved') ? (
      <span
        role="status"
        data-autosave={autoSaid ?? 'pending'}
        title={autoFailed ? autoRefusal : undefined}
        className={cn('text-xs whitespace-nowrap', autoFailed ? 'text-destructive' : 'text-muted-foreground')}
      >
        {autoFailed ? 'Not saved' : autoSaid === 'saved' && !changed ? 'Saved' : 'Saving…'}
      </span>
    ) : null;
  const [controlsEnd, setControlsEnd] = React.useState<Nullable<HTMLElement>>(null);
  const scrolled = useScrolledPast(controlsEnd);
  const headerHost = React.useContext(HeaderHost);
  const refresh = { reading: list.reading, readAt: read?.readAt ?? null, onClick: () => void list.refresh() };
  const help = (
    <HelpPopover label="About Lists" align="end">
      <p>
        A row is one participant’s newest response to the survey. Pick a view, filter it, edit values
        in the grid and press Save data; Save view keeps the view’s filter, columns, sort, grouping,
        colours and page size — they belong to the view, not to the address.
      </p>
      <p>
        A private view of yours saves itself as you change it. A shared view is saved with Save view.
        A condition with a lock is pinned by the view’s owner: it stays on for everyone who opens the
        view, and your own filters are added beside it.
      </p>
      <p>
        Each row opens in the reading pane by its first icon and in a new tab by ↗; View, Edit and a
        click on the row follow «Opens in». The pane sits beside the list from 1440 px wide and over
        it as a sheet on a narrower screen; «Opens in» also sets how it is laid out.
      </p>
      <p>
        Every 30 seconds the list brings in what colleagues changed: a row that no longer matches is
        struck through, new matching rows are announced, and Refresh shows them.
      </p>
    </HelpPopover>
  );
  const writes = (
    <>
      <Button
        type="button"
        className="h-8 px-3"
        disabled={!canWrite || pendingCount === 0 || edits.saving}
        aria-describedby={canWrite ? undefined : writeReason}
        onClick={() => {
          setTicked(new Set());
          void edits.save();
        }}
      >
        {edits.saving ? 'Saving…' : `Save data${pendingCount > 0 ? ` (${pendingCount})` : ''}`}
      </Button>
      <Button
        type="button"
        variant="ghost"
        className="h-8 px-2.5 font-normal"
        disabled={pendingCount === 0 || edits.saving}
        onClick={edits.discard}
      >
        Discard
      </Button>
    </>
  );
  const actionProps = {
    survey,
    query: listQuery,
    viewId: view?.id ?? null,
    total,
    catalogue: editableSections,
    selectedRows: tickedRows,
    pageRows: read?.rows ?? [],
    accounts,
    canWrite,
    canAssign,
    canBulk,
    canRecycle: canCorrect,
    canAccess: can('rankings.manage'),
    canReadCycles: can('cycles.manage'),
    staged: pendingCount > 0,
    saving: edits.saving,
    answersLocked,
    accountsUnread,
    onStage: (rows: readonly IListRow[], field: IListField, ui: Parameters<typeof edits.stage>[2]) => {
      for (const row of rows) edits.stage(row, field, ui);
    },
    onSaveNow: (rows: readonly IListRow[], field: IListField, ui: Parameters<typeof edits.stage>[2]) => {
      if (edits.saving) return;
      setTicked(new Set());
      void edits.saveNow([...rows], field, ui);
    },
    onMassUpdated: (said: string) => {
      setMassSaid(said);
      void list.poll();
    },
    // The page afresh, not a poll: a poll strikes a row that left, and a moved row is gone.
    onRecycled: async (run: IRecycleRun) => {
      setRecycleSaid(run);
      setTicked(new Set());
      await list.refresh();
    },
    onAccessChanged: setMassSaid,
    onExport: () => setExporting(true),
    onSaveAsReport: () => setReporting(true)
  };
  const viewPicker = (buttonMaxWidth?: string) => (
    <ViewPicker
      buttonMaxWidth={buttonMaxWidth}
      survey={survey}
      current={view ? { id: view.id, name: view.name, visibility: view.visibility } : null}
      changed={changed && (!autosaves || autoFailed)}
      unsavedName={fresh ? 'New list — not saved' : undefined}
      onOpen={(viewId) => guard.ask(() => onOpenView(viewId))}
      onNew={() => guard.ask(onNew)}
      // A rename or a new default from *Select a view*: the view on screen takes it in place, its
      // unsaved changes kept.
      onSaved={(next) => {
        if (next.body.id === view?.id) setSaved(next);
      }}
      onDeleted={(viewId) => {
        if (viewId !== view?.id) return;
        setSaved(null);
        onHand(null);
      }}
    />
  );
  const picker = viewPicker();
  const filtersButton = (
    <FiltersButton
      root={root}
      words={words}
      sections={filterSections}
      onField={filterOn}
      onAdvanced={() => openAdvanced()}
      onChange={change(setRoot)}
    />
  );
  const columnsButton = <ColumnsSheet catalogue={catalogue} columns={columns} onChange={change(setColumns)} />;
  const readingPane = (row: IListRow, open: { key: string; mode: PaneMode }) => (
    <ReadingPane
      row={row}
      columns={shownColumns}
      catalogue={catalogue}
      mode={open.mode}
      onMode={(mode) => setPane({ ...open, mode })}
      onClose={() => setPane(null)}
      editOf={editOf}
      editLocked={canWrite ? null : writeReason}
    />
  );
  const chipLine = chips.length > 0 && <CompactChipLine label="Filters" chips={chips} onClearAll={clearAll} />;

  return (
    <div className="grid min-w-0 gap-3">
      {headerHost &&
        createPortal(
          <>
          {/* From 1366 px the view sits in the header (SR-412): the list gains a line. Its unsaved
              state is the picker's `*`; a private view's own save says itself beside Save view. */}
          {viewInHeader && (
            <span data-view-bar="" className="flex min-w-0 items-center gap-2 text-[0.8125rem]">
              {/* What the header has left beside the survey tabs, Save view, the count and "?" — so
                  the header stays one line; the name is cut with "…", whole in the list. */}
              {viewPicker(
                `clamp(10rem, calc(100vw - ${68 + (autosaves ? 3.5 : 0) + (autoMark ? 3.5 : 0) + (view?.migratedFrom ? 5.5 : 0)}rem), 26rem)`
              )}
              {viewSaves.menu}
              {autoMark}
              {view?.migratedFrom && <MigratedChip view={view} short />}
            </span>
          )}
          <WorkPageHeaderActions
            summary={total === null ? null : countOf(total, 'row')}
            summaryData={{ 'data-lists-summary': '' }}
            refresh={refresh}
            help={help}
          />
          </>,
          headerHost
        )}
      <CompactBar
        shown={scrolled}
        label="Lists, compact"
        actions={<WorkPageHeaderActions summary={null} refresh={refresh} help={help} />}
        below={chipLine}
      >
        <CompactTitle>Lists</CompactTitle>
        {/* A survey's menu leaves the screen: offered only while nothing is pending, as the tabs ask. */}
        {pendingCount === 0 ? <SurveyMenu current={survey} /> : <CompactTitle>{listSurveyLabel(survey)}</CompactTitle>}
        {picker}
        <CompactDivider />
        <CompactSearch {...nameSearch} />
        {filtersButton}
        <span className="flex-1" />
        <CompactDivider />
        {writes}
        <ListActions {...actionProps} />
        {columnsButton}
        <CompactDivider />
      </CompactBar>

      {!viewInHeader && (
        <ListViewBar
          picker={picker}
          mark={
            <>
              {autoMark}
              {view?.migratedFrom && <MigratedChip view={view} />}
            </>
          }
          saveView={viewSaves.menu}
          scope={teamPath}
        />
      )}

      {/* The toolbar and the chip line under it, one block: the compact bar takes over once both have
          scrolled away, and its marker sits on the block's bottom edge, taking no row of its own (a
          zero-height row would still get the grid's gap). */}
      <div className="relative grid min-w-0 gap-3">
        <WorkToolbar
          toolbar={toolbar}
          status={{ reading: list.reading, count: total === null ? undefined : countOf(total, 'row') }}
          search={
            <form
              role="search"
              className="flex min-w-0 flex-1"
              onSubmit={(event) => {
                event.preventDefault();
                nameSearch.onSubmit();
              }}
            >
              <FilterBox
                label={nameSearch.label}
                placeholder={nameSearch.placeholder}
                value={nameSearch.draft}
                onChange={nameSearch.onDraft}
                className="flex-1"
              />
            </form>
          }
          lead={viewInHeader && teamPath}
          quick={null}
          quickCount={0}
          after={filtersButton}
          end={
            <>
              {writes}
              <ListActions {...actionProps} />
              {columnsButton}
              <PaneControl prefs={panePrefs} />
            </>
          }
        />
        {chipLine}
        <div ref={setControlsEnd} aria-hidden data-controls-end="" className="absolute bottom-0 h-0 w-full" />
      </div>
      {chipProblems.length > 0 && (
        <ul role="alert" className="text-[0.8125rem] text-destructive">
          {chipProblems.map((said, index) => (
            <li key={index}>{said}</li>
          ))}
        </ul>
      )}

      {viewSaves.notice}
      {!canWrite && <NotPermitted id={writeReason} what="editing cells in a list" />}
      {canWrite && !canAssign && columns.includes(ASSIGNED_TO) && (
        <NotPermitted id={assignReason} what="assigning rows — Assigned to is read here" />
      )}
      {canWrite && !canCorrect && answerShown && (
        <NotPermitted id={correctReason} what="correcting survey answers — the answers are read here" />
      )}
      {accountsUnread && (
        <p role="alert" className="text-sm text-destructive">
          The accounts to assign could not be read — Assigned to is read here. {assignees.error}
        </p>
      )}
      {edits.banner && (
        <p role="alert" className="text-sm text-destructive">
          {edits.banner}
        </p>
      )}


      <GroupStrip
        groupBy={groupBy}
        columns={columns}
        fieldOf={fieldOf}
        aggregates={read?.aggregates ?? null}
        onChange={change(setGroupBy)}
      />

      {error && read !== null && (
        <LoadError message={`Could not refresh — showing the list as last read. ${error.message}`} />
      )}
      {list.pollError && read !== null && !error && (
        <p role="status" className="text-sm text-muted-foreground">
          Could not refresh — showing the list as last read. {list.pollError}
        </p>
      )}
      {columns.length === 0 && (
        <p className="text-sm text-muted-foreground">This view shows no columns — add them from Columns.</p>
      )}
      {hiddenColumns > 0 && (
        <p className="text-sm text-muted-foreground">
          This view names {hiddenColumns === 1 ? 'a column' : `${hiddenColumns} columns`} you cannot
          see; {hiddenColumns === 1 ? 'it is' : 'they are'} left out.
        </p>
      )}
      <p role="status" aria-live="polite" className="text-sm text-muted-foreground empty:hidden">
        {[
          edits.status ? `${edits.status}.` : null,
          massSaid ? `${massSaid}.` : null,
          recycleSaid ? runSaid(recycleSaid) : null,
          struckOnPage > 0
            ? `${struckOnPage} ${struckOnPage === 1 ? 'row no longer matches' : 'rows no longer match'} this list.`
            : null,
          list.entered > 0
            ? `${list.entered} ${list.entered === 1 ? 'row joined' : 'rows joined'} this list — Refresh to see ${list.entered === 1 ? 'it' : 'them'}.`
            : null
        ]
          .filter(Boolean)
          .join(' ')}
      </p>

      <SelectionBar count={tickedRows.length} onClear={() => setTicked(new Set())} stickyTop={STICKY_BAR_TOP}>
        <ListActions {...actionProps} part="selection" />
      </SelectionBar>

      {/* The pane's column only while a pane is drawn beside the list (the reader's layout, else
          beside from 1440 px): reserved always, it left the grid a blank beside it. Its width
          follows the screen's, so its controls are not held to a fixed 26rem. */}
      <div
        className={cn(
          'grid items-start gap-3',
          paneRow && pane && panePrefs.layout === 'beside' && 'grid-cols-[minmax(0,1fr)_min(34vw,40rem)]'
        )}
      >
      <div className="grid min-w-0 gap-3" onFocus={follow}>
      <ListGrid
        label={`${listSurveyLabel(survey)} list`}
        columns={shownColumns}
        fieldOf={fieldOf}
        sourceOf={sourceOf}
        rows={read?.rows ?? null}
        state={tableState}
        struck={list.struck}
        sorts={sorts}
        onSort={change(setSorts)}
        root={root}
        query={readQuery}
        kindOf={kindOf}
        words={words}
        filtering={{
          onApply: change(setRoot),
          base: pinnedWire,
          onAdvanced: () => openAdvanced(),
          open: funnel,
          onOpen: setFunnel
        }}
        groupBy={groupBy}
        collapsed={collapsed}
        onToggleGroup={(key) =>
          setCollapsed((current) => {
            const next = new Set(current);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
          })
        }
        aggregates={read?.aggregates ?? null}
        sums={sums}
        rules={rules}
        editOf={editOf}
        selection={selection}
        actions={{
          onView: viewRow,
          onPane: viewRow,
          viewInNewTab: viewOpens === 'tab',
          onEdit: (row) => openPane(row, 'edit'),
          onNote: setNoting,
          editLocked: canWrite ? null : writeReason
        }}
        open={{
          key: paneRow ? rowKeyOf(paneRow) : null,
          // A row's press, Enter or a plain click on the name follows *Opens in* (SR-412): the
          // record page in a new tab, or the pane.
          onOpen: (row) =>
            viewOpens === 'tab'
              ? window.open(recordSurveyPath(row.participantId, row.survey), '_blank', 'noopener')
              : openPane(row, pane?.mode ?? 'view')
        }}
        stickyTop={STICKY_TABLE_TOP}
      />

      <div
        data-grid-footer=""
        className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[0.8125rem] text-muted-foreground"
      >
        <Legend rules={rules} words={words} />
        {read?.aggregatesError ? (
          <span>The totals could not be read: {read.aggregatesError}</span>
        ) : (
          sums.map((field) => {
            const sum = read?.aggregates?.sums[field.code];
            return (
              <span key={field.code} data-total={field.code} className="tabular-nums">
                Σ {field.label}{' '}
                <span className="font-medium text-foreground">
                  {read?.aggregates ? (sum === null || sum === undefined ? '—' : formatListValue(field, sum)) : ''}
                </span>
              </span>
            );
          })
        )}
      </div>
      {read && (
        <WorkPager
          page={page}
          perPage={size}
          perPages={[...new Set([...LIST_PAGE_SIZES, size])].sort((a, b) => a - b)}
          pages={pages}
          maxShown={page === 1 ? LIST_MAX_PAGE_SIZE : size}
          total={read.total}
          busy={list.reading}
          onPage={setPage}
          onPerPage={(next) => change(setPageSize)(next)}
          onMore={() => setWindow(1, pages + 1)}
          sticky
        />
      )}
      </div>
      {paneRow && pane && panePrefs.layout !== 'sheet' && readingPane(paneRow, pane)}
      </div>
      {/* A sheet over the list (SR-412): the default below 1440 px; Esc or ✕ closes it. */}
      <Sheet open={paneRow !== null && pane !== null && panePrefs.layout === 'sheet'} onOpenChange={(open) => !open && setPane(null)}>
        <SheetContent side="right" className="w-[min(40rem,calc(100vw-2rem))] gap-0 overflow-y-auto p-3">
          <SheetTitle className="sr-only">{paneRow ? nameOf(paneRow) : 'Reading pane'}</SheetTitle>
          {paneRow && pane && readingPane(paneRow, pane)}
        </SheetContent>
      </Sheet>

      <ConflictDialog
        asked={edits.asked}
        fieldOf={fieldOf}
        accounts={accounts}
        onKeepMine={(asked) => void edits.keepMine(asked)}
        onTakeTheirs={edits.takeTheirs}
      />

      <FormattingSheet
        open={formatting}
        onOpenChange={setFormatting}
        rules={rules}
        catalogue={catalogue}
        words={words}
        kindOf={kindOf}
        accounts={assignees.data ?? undefined}
        problems={ruleProblems(viewSaves.problems)}
        onApply={(next) => {
          setRules(next);
          viewSaves.clearProblems();
        }}
      />

      <ExportDialog
        open={exporting}
        onClose={() => setExporting(false)}
        survey={survey}
        query={listQuery}
        shownColumns={shownColumns.length}
        total={total}
      />

      <SaveAsReportDialog open={reporting} onClose={() => setReporting(false)} survey={survey} query={listQuery} />
      {noting && (
        <RowNotesDialog
          row={noting}
          onAppended={(cell) => list.adopt([cell])}
          onClose={() => setNoting(null)}
        />
      )}

      <AdvancedFilterSheet
        open={advanced.open}
        onOpenChange={(open) => setAdvanced((current) => ({ ...current, open }))}
        root={root}
        catalogue={catalogue}
        words={words}
        placed={placed}
        accounts={assignees.data ?? undefined}
        focusId={advanced.focusId}
        addField={advanced.addField}
        onApply={change(setRoot)}
      />
    </div>
  );
}

/** `nodes` ANDed with the filter (SR-412): added to an *all* filter, or one of one condition, else
 *  beside it whole — `(A or B) and P`, never `A or B or P`. */
function andWith(root: ICriteriaGroup, nodes: readonly CriteriaNode[]): ICriteriaGroup {
  if (root.op === 'and' || root.children.length <= 1) return { ...root, op: 'and', children: [...root.children, ...nodes] };
  return { ...root, id: `${root.id}-and`, op: 'and', children: [root, ...nodes] };
}

/** Problems placed on several trees, as one. */
function joinPlaced(all: readonly IPlacedCriteriaProblems[]): IPlacedCriteriaProblems {
  const byNode = new Map<string, readonly string[]>();
  for (const one of all) {
    for (const [id, reasons] of one.byNode) byNode.set(id, [...(byNode.get(id) ?? []), ...reasons]);
  }
  const unplaced = all.flatMap((one) => one.unplaced);
  return byNode.size === 0 && unplaced.length === 0 ? NO_PROBLEMS : { byNode, unplaced };
}

/** A query's member a refusal names by index: its words, and the field code at that index. */
const QUERY_MEMBERS: Readonly<
  Record<'columns' | 'sorts' | 'groupBy', { what: string; code: (query: IListQuery, index: number) => string | undefined }>
> = {
  columns: { what: 'The column', code: (query, index) => query.columns?.[index] },
  sorts: { what: 'The sort by', code: (query, index) => query.sorts?.[index]?.field },
  groupBy: { what: 'The grouping by', code: (query, index) => query.groupBy?.[index]?.field }
};

/** A refusal no chip carries, in words: the column, sort or grouping it names by its label. */
export function sayProblem(
  problem: ICriteriaProblem,
  query: IListQuery,
  fieldOf: (code: string) => IListField | undefined
): string {
  const at = /^query\.(columns|sorts|groupBy)\[(\d+)\]/.exec(problem.target);
  if (at) {
    const member = QUERY_MEMBERS[at[1] as keyof typeof QUERY_MEMBERS];
    const code = member.code(query, Number(at[2]));
    return `${member.what} ${code ? (fieldOf(code)?.label ?? code) : ''} ${problem.reason}`.replace(/\s+/g, ' ');
  }
  if (problem.target.startsWith('query.scope.path')) return `The team path ${problem.reason}`;
  if (problem.target.startsWith('query.criteria')) return `A condition ${problem.reason}`;
  return problem.reason;
}

/** The Teams survey's team paths to choose from: All teams, Wirehouse, Independent — and a path the
 *  view holds that none of them is, spelled as stored. */
function teamPathChoices(path: Nullable<string>): { code: Nullable<string>; label: string }[] {
  return [
    { code: null, label: 'All teams' },
    ...TEAM_PATH_CHOICES,
    ...(path !== null && !TEAM_PATH_CHOICES.some((choice) => choice.code === path) ? [{ code: path, label: path }] : [])
  ];
}

/** *Team path* as one menu — the list's toolbar and view line, where the pills take too much room. */
function TeamPathMenu({ path, onChoose }: { path: Nullable<string>; onChoose: (path: Nullable<string>) => void }) {
  const choices = teamPathChoices(path);
  const label = choices.find((choice) => choice.code === path)?.label ?? 'All teams';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" className="h-8 gap-1 px-2.5 font-normal" aria-label={`Team path: ${label}`}>
          <span className="text-muted-foreground">Team path:</span> {label}
          <ChevronDown className="size-4" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuRadioGroup value={path ?? ''} onValueChange={(next) => onChoose(next === '' ? null : next)}>
          {choices.map((choice) => (
            <DropdownMenuRadioItem key={choice.code ?? ''} value={choice.code ?? ''}>
              {choice.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The Teams survey's *Team path* chips: All teams, Wirehouse, Independent. */
export function TeamPathChoice({
  path,
  onChoose
}: {
  path: Nullable<string>;
  onChoose: (path: Nullable<string>) => void;
}) {
  const choices = teamPathChoices(path);
  return (
    <div role="radiogroup" aria-label="Team path" className="flex flex-wrap items-center gap-1.5 text-[0.8125rem]">
      <span className="mr-1 text-xs font-medium text-[var(--veritant-royal-70)]">Team path</span>
      {choices.map((choice) => {
        const on = choice.code === path;
        return (
          <button
            key={choice.code ?? ''}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChoose(choice.code)}
            className={cn(
              'h-7 rounded-full border px-3 outline-none focus-visible:ring-2 focus-visible:ring-ring',
              on
                ? 'border-[var(--color-teal)] bg-[color-mix(in_srgb,var(--color-teal)_10%,white)] font-medium text-[var(--color-royal-blue)]'
                : 'border-border bg-white text-muted-foreground hover:text-[var(--color-royal-blue)]'
            )}
          >
            {choice.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * A migrated view's line (§3.3; survey-api's §12): "Carried over from Salesforce — 3 columns and 1
 * condition could not be carried. What was not carried" — the popover listing each part survey-api
 * names, its Salesforce name and survey-api's reason as sent. **Only a part marked lost is counted**
 * (SR-408's R1, the owner's Q1): since !103 the list also names parts carried with nothing lost —
 * listed apart, under "Carried with nothing lost", never counted — and a part without the marker
 * (a view migrated before it, an API before it) is a loss, as every part was. The reason is never
 * read to decide which. The view opens and works with what was carried; the line is there so staff
 * repair it.
 */
export function MigratedLine({ view }: { view: ISavedView }) {
  const parts = view.migratedFrom?.unmapped ?? [];
  const lost = parts.filter((part) => part.lost !== false);
  const kept = parts.filter((part) => part.lost === false);
  return (
    <p data-migrated="" className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
      <span>
        Carried over from Salesforce
        {migratedEnding(lost, kept)}
      </span>
      {parts.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" variant="link" className="h-auto p-0 text-sm">
              What was not carried
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            aria-label="What was not carried"
            className="grid max-h-[70dvh] w-96 max-w-[calc(100vw-2rem)] gap-3 overflow-y-auto p-3 text-[0.8125rem]"
          >
            <MigratedDetails lost={lost} kept={kept} />
          </PopoverContent>
        </Popover>
      )}
    </p>
  );
}

/**
 * Lists' mark of a migrated view (SR-412): a tag beside *Save view* rather than a line of its own —
 * "From Salesforce", or "From Salesforce · 3 lost" in the attention tone where something could not
 * be carried; its press opens the same sentence and lists as `MigratedLine`.
 */
function MigratedChip({ view, short = false }: { view: ISavedView; short?: boolean }) {
  const parts = view.migratedFrom?.unmapped ?? [];
  const lost = parts.filter((part) => part.lost !== false);
  const kept = parts.filter((part) => part.lost === false);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-migrated=""
          aria-label={`Carried over from Salesforce${migratedEnding(lost, kept)}`}
          title={short ? `Carried over from Salesforce${migratedEnding(lost, kept)}` : undefined}
          className={cn(
            'inline-flex h-6 shrink-0 items-center gap-1 rounded-full border px-2 text-xs whitespace-nowrap outline-none focus-visible:ring-2 focus-visible:ring-ring',
            lost.length > 0
              ? 'border-[color-mix(in_srgb,var(--color-yellow)_70%,black)] bg-[color-mix(in_srgb,var(--color-yellow)_30%,white)] text-[var(--color-midnight-blue)]'
              : 'border-border bg-white text-muted-foreground hover:text-[var(--color-royal-blue)]'
          )}
        >
          {short ? 'SF' : 'From Salesforce'}
          {lost.length > 0 && ` · ${lost.length} lost`}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        aria-label="Carried over from Salesforce"
        className="grid max-h-[70dvh] w-96 max-w-[calc(100vw-2rem)] gap-3 overflow-y-auto p-3 text-[0.8125rem]"
      >
        <p>
          Carried over from Salesforce
          {migratedEnding(lost, kept)}
        </p>
        <MigratedDetails lost={lost} kept={kept} />
      </PopoverContent>
    </Popover>
  );
}

/** How the migrated line's sentence ends: what was lost, else that nothing was, else a full stop
 *  (a view whose `unmapped` names no part). */
function migratedEnding(lost: readonly IMigratedPart[], kept: readonly IMigratedPart[]): string {
  if (lost.length > 0) return ` — ${lostSummary(lost)} could not be carried.`;
  if (kept.length > 0) return ' with nothing lost.';
  return '.';
}

/** What a migrated view's popover lists: the parts lost, then those carried with nothing lost. */
function MigratedDetails({ lost, kept }: { lost: readonly IMigratedPart[]; kept: readonly IMigratedPart[] }) {
  return (
    <>
      {lost.length > 0 && <MigratedParts parts={lost} name="Lost" />}
      {kept.length > 0 && (
        <div className="grid gap-1.5">
          <p className="text-xs font-medium tracking-wide text-[var(--veritant-royal-70)] uppercase">
            Carried with nothing lost
          </p>
          <MigratedParts parts={kept} name="Carried with nothing lost" />
        </div>
      )}
    </>
  );
}

/** One list of a migrated view's parts — each its part, its Salesforce name, survey-api's reason. */
function MigratedParts({ parts, name }: { parts: readonly IMigratedPart[]; name: string }) {
  return (
    <ul aria-label={name} className="grid gap-2">
      {parts.map((part, index) => (
        <li key={index} className="grid gap-0.5">
          <span className="font-medium">
            {migratedPartLabel(part.part)}
            {salesforceName(part.sf) && (
              <span className="font-normal text-muted-foreground"> — {salesforceName(part.sf)}</span>
            )}
          </span>
          {part.reason && <span className="text-muted-foreground">{part.reason}</span>}
        </li>
      ))}
    </ul>
  );
}

/** Salesforce's name for a part, as survey-api kept it — a string as sent, an object's string
 *  members joined; never a document. */
function salesforceName(sf: unknown): string {
  if (typeof sf === 'string' || typeof sf === 'number' || typeof sf === 'boolean') return String(sf);
  if (sf !== null && typeof sf === 'object' && !Array.isArray(sf)) {
    return Object.values(sf)
      .filter((value): value is string | number => typeof value === 'string' || typeof value === 'number')
      .join(' · ');
  }
  return '';
}
